import { useEffect, useState } from "react";
import type { TranslateFn } from "../../i18n";

const TARGET_SECONDS = 22;

export function TryOnGenerationBadge({
  tryOnStartedAt,
  t,
}: {
  tryOnStartedAt: number | null;
  t: TranslateFn;
}) {
  const [, force] = useState(0);
  useEffect(() => {
    if (tryOnStartedAt == null) return;
    const id = setInterval(() => force((v) => v + 1), 200);
    return () => clearInterval(id);
  }, [tryOnStartedAt]);

  if (tryOnStartedAt == null) return null;

  const elapsed = (Date.now() - tryOnStartedAt) / 1000;
  const pct = Math.min(95, (elapsed / TARGET_SECONDS) * 100);
  const pctRounded = Math.round(pct);
  const remaining = Math.max(0, TARGET_SECONDS - Math.floor(elapsed));
  const etaText = elapsed >= TARGET_SECONDS ? t("almost done") : `~${remaining}s ${t("left")}`;

  const statuses = [
    t("Preparing your image"),
    t("Analyzing body proportions"),
    t("Mapping garment to body"),
    t("Refining drape and shadows"),
    t("Almost done"),
  ];
  const stepIdx = Math.min(
    statuses.length - 1,
    Math.floor((elapsed / TARGET_SECONDS) * statuses.length),
  );
  const status = statuses[stepIdx];

  return (
    <div className="ps-tryon-badge" role="status" aria-live="polite">
      <div className="ps-tryon-badge-row">
        <span className="ps-tryon-badge-spinner" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <defs>
              <linearGradient id="ps-tryon-badge-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#2563EB" />
              </linearGradient>
            </defs>
            <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(59,130,246,0.18)" strokeWidth="2.4" />
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke="url(#ps-tryon-badge-grad)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeDasharray="56.5"
              strokeDashoffset="38"
            />
          </svg>
        </span>
        <span className="ps-tryon-badge-title">{t("Generating your look...")}</span>
        <span className="ps-tryon-badge-pct">{pctRounded}%</span>
      </div>
      <div className="ps-tryon-badge-bar" aria-hidden="true">
        <div className="ps-tryon-badge-bar-fill" style={{ width: `${pctRounded}%` }} />
      </div>
      <div className="ps-tryon-badge-foot">
        <span className="ps-tryon-badge-status">
          <span className="ps-tryon-badge-status-icon" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="6" y1="10" x2="6" y2="14" />
              <line x1="10" y1="6" x2="10" y2="18" />
              <line x1="14" y1="9" x2="14" y2="15" />
              <line x1="18" y1="11" x2="18" y2="13" />
            </svg>
          </span>
          {status}
        </span>
        <span className="ps-tryon-badge-eta">
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {etaText}
        </span>
      </div>
    </div>
  );
}
